import React, { useState, useEffect } from 'react'
import { AppButton, AppTextInput } from '@easyllama/ui-components'
import { Plus, Minus, PlusCircle, MinusCircle } from 'lucide-react'
import { MIN_COUNT, SEAT_STEP } from '~/service/Billing/billingConstants'
import clsx from 'clsx'

export default function PlanCounter({ value, onChange, disabled, circleIcons = false, compact = false, autoSize = false, minCount = MIN_COUNT, step = SEAT_STEP }) {
  // The plain mode (neither compact nor autoSize) is the only one that stands alone in its own
  // row rather than sitting inline next to a label or fitting its content width, so it's the only
  // one that should grow to fill a narrow viewport - a property of the mode itself, not a flag
  // callers need to opt into.
  const fillsRowOnMobile = !compact && !autoSize
  const [inputValue, setInputValue] = useState(String(value))
  const isDirtyRef = React.useRef(false)

  useEffect(() => {
    setInputValue(String(value))
  }, [value])

  const decrement = () => {
    const next = Math.max(minCount, value - step)
    setInputValue(String(next))
    onChange(next)
  }

  const increment = () => {
    const next = value + step
    setInputValue(String(next))
    onChange(next)
  }

  const handleBlur = () => {
    if (!isDirtyRef.current) return
    isDirtyRef.current = false
    const n = parseInt(inputValue, 10)
    const clamped = Number.isFinite(n) ? Math.max(minCount, Math.round(n / step) * step) : minCount
    setInputValue(String(clamped))
    onChange(clamped)
  }

  return (
    <div className={clsx('flex items-center gap-1', compact ? 'justify-start' : 'justify-center', disabled && 'cursor-not-allowed', fillsRowOnMobile && 'max-lg:w-full')}>
      <AppButton
        variant='ghost'
        size='icon'
        disabled={disabled || value <= minCount}
        className={clsx('disabled:cursor-not-allowed', compact && '!w-[38px] !h-[38px]')}
        onClick={decrement}
      >
        {circleIcons ? <MinusCircle className='text-llama-grey-7' size={20} /> : <Minus className='text-llama-grey-7' size={16} />}
      </AppButton>

      <AppTextInput
        type={compact ? undefined : 'number'}
        min={minCount}
        step={step}
        disabled={disabled}
        value={inputValue}
        onBlur={handleBlur}
        onChange={(e) => {
          if (!compact) {
            isDirtyRef.current = true;
            setInputValue(e.target.value)
          }
        }}
        // AppTextInput's own root (styled via wrapperClassName, not className) is the actual flex
        // item in this row - className only reaches the inner <input>, so growing that alone has
        // no effect on the row's layout. Both need the override: wrapperClassName claims the
        // available row space, className then lets the input itself fill that reserved space.
        wrapperClassName={clsx(fillsRowOnMobile && 'max-lg:flex-1 max-lg:min-w-0')}
        className={clsx(
          'text-center lining-nums disabled:cursor-not-allowed [appearance:textfield] [&::-webkit-inner-spin-button]:hidden [&::-webkit-outer-spin-button]:hidden',
          compact ? '!w-13' : !autoSize && 'w-16',
          fillsRowOnMobile && 'max-lg:!w-full',
        )}
        style={autoSize ? { fieldSizing: 'content', minWidth: '8ch' } : undefined}
        readOnly={compact}
      />
      <AppButton
        variant='ghost'
        size='icon'
        disabled={disabled}
        className={clsx('disabled:cursor-not-allowed', compact && '!w-[38px] !h-[38px]')}
        onClick={increment}
      >
        {circleIcons ? <PlusCircle className='text-llama-grey-7' size={20} /> : <Plus className='text-llama-grey-7' size={16} />}
      </AppButton>

    </div>
  )
}
